import LoadingContent from "@/components/LoadingContent";
import { useRowSelection } from "@/contexts/RowSelection/useRowSelection";
import { useGetRecordsLibrary } from "@/hooks/queries/useGetRecordsLibrary";
import { useCustomParams } from "@/hooks/useCustomParams";
import { FullPagination, FullPaginationVariant, ListRowModel, LoaderSize, VerticalFlex } from "@enxcs/dap-ui-stratos";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RecordingBulkActions } from "../RecordingBulkActions";
import { getHeadings } from "../RecordingTable/RecordingTableHeaders";
import { PaginationFooter, StyledTableBody, StyledTableHead, TableLayout, TableStyled } from "./RecordingListTable.styled";

export type Props = {
  tableStructure: ListRowModel[];
};

const RecordingListTable: FC<Props> = ({ tableStructure }) => {
  const { t } = useTranslation();

  const { isLoading, data: recordsList = { items: [], currentPage: 1, pageSize: 25, totalItems: 0, totalPages: 0 } } = useGetRecordsLibrary();

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useCustomParams();

  const { onResetExpandedRow, selecteds, onSelect, onSelectAll, isSelectDisabled } = useRowSelection();

  const headings = getHeadings();

  const selectedKeys = Array.from(selecteds.keys());

  const selectableItems = recordsList?.items?.filter((recording) => !isSelectDisabled(recording));
  const isAllSelected = selectableItems.length > 0 && selectableItems.every((recording) => selecteds.has(recording.recordingId));
  const hasPagination = recordsList.pageSize < recordsList.totalItems;

  const handlePageChange = useCallback(
    (newPage: number) => {
      onChangePage(newPage);
      onResetExpandedRow();
    },
    [onChangePage, onResetExpandedRow],
  );

  const handleRowsPerPageChange = useCallback(
    (newPage: number) => {
      onChangeRowsPerPage(newPage);
      onResetExpandedRow();
    },
    [onChangeRowsPerPage, onResetExpandedRow],
  );

  const handleSelectAll = (value: boolean) => {
    const items = selectableItems.map((recording) => ({ row: recording.recordingId, exports: new Set((recording.exports ?? []).map((e) => e.id)) }));

    onSelectAll(items, value);
  };

  const handleOnSelectRows = (key: string) => {
    const recording = recordsList.items?.find((i) => i.recordingId === key);
    if (recording) {
      const { exports } = recording;

      if (isSelectDisabled(recording)) return;

      const value = { row: key, exports: new Set((exports ?? []).map((e) => e.id)) };

      onSelect(key, value);
    }
  };

  return (
    <VerticalFlex $grow>
      <RecordingBulkActions totalItems={recordsList.totalItems} hasPagination={hasPagination} />
      <TableLayout>
        <TableStyled role="table" id="recordings-list-table">
          <StyledTableHead
            headings={headings}
            selectedAll={isAllSelected}
            selectAllDisabled={isLoading || selectableItems.length === 0}
            onSelectAll={handleSelectAll}
          />

          <StyledTableBody
            headings={headings}
            className="border-none"
            selectedKeys={isLoading ? undefined : selectedKeys}
            onSelectRow={handleOnSelectRows}
            rows={
              !isLoading
                ? tableStructure
                : [
                    {
                      key: `loadingPage${page}-rowsPerPage${rowsPerPage}`,
                      cells: [
                        {
                          content: <LoadingContent loaderSize={LoaderSize.V56} loaderHeight={"290px"} loaderWidth={"100%"} />,
                        },
                      ],
                    },
                  ]
            }
          />
        </TableStyled>

        {recordsList && !isLoading && hasPagination && (
          <PaginationFooter>
            <FullPagination
              page={recordsList.currentPage}
              totalCount={recordsList.totalItems}
              rowsPerPage={recordsList.pageSize}
              rowsPerPageLabel={t("recordsList.rowsPerPage")}
              ofLabel={t("recordsList.of")}
              rowsLabel=""
              pagesLabel=""
              rowsPerPageItems={[10, 25, 50, 75, 100]}
              previousButtonTitle={t("recordsList.previousPage")}
              nextButtonTitle={t("recordsList.nextPage")}
              variant={FullPaginationVariant.TABLE}
              ariaLabel={t("recordsList.paginationLabel")}
              ariaControls="recordings-list-table"
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </PaginationFooter>
        )}
      </TableLayout>
    </VerticalFlex>
  );
};

export default RecordingListTable;
